import {
  useEffect,
  useRef,
  useState,
  useId,
  cloneElement,
  isValidElement,
  type FormEvent,
  type ReactNode,
  type ReactElement,
} from 'react';
import { Check, Download, FileText, Plus, RefreshCw, X } from 'lucide-react';
import { useApi } from '../lib/api';
import {
  privateProjectUrl,
  projectMoney,
  type ConciergeProjectPayload,
  type ProjectDocument,
  type ProjectDocumentDraft,
  type ProjectInstaller,
  type ProjectLibraryFile,
} from '../lib/concierge-project';

const input =
  'mt-1 block w-full min-h-11 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100';
const button =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50';
const primary = `${button} !bg-black !text-white !border-black hover:!bg-slate-800`;
const muted = 'text-sm leading-6 text-slate-600';
const statusLabel = (s?: string) => (s || 'Not started').replace(/[-_]/g, ' ');
const date = (s?: string) => (s ? new Date(s).toLocaleString() : 'Not set');
const steps = [
  'received',
  'reviewing',
  'awaiting-customer',
  'sourcing-installer',
  'quoting',
  'approved',
  'scheduled',
  'installing',
  'completed',
  'closed',
];
const blankDocument = (kind: 'quote' | 'invoice'): ProjectDocumentDraft => ({
  kind,
  title: kind === 'quote' ? 'Installation quote' : 'Installation invoice',
  currency: 'USD',
  lineItems: [
    { description: '', quantity: 1, unit: 'each', unitPriceCents: Number.NaN },
  ],
  taxes: [],
  notes: '',
  terms: '',
  issuer: {
    name: '',
    address: '',
    email: '',
    taxRegistration: '',
    paymentInstructions: '',
  },
});
const blankInstaller = (): ProjectInstaller => ({
  name: '',
  email: '',
  phone: '',
  serviceArea: '',
  website: '',
  publicDescription: '',
  internalNotes: '',
});
function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {isValidElement(children)
        ? cloneElement(
            children as ReactElement<{
              id?: string;
              'aria-describedby'?: string;
            }>,
            { id, 'aria-describedby': hint ? `${id}-hint` : undefined },
          )
        : children}
      {hint && (
        <p
          id={`${id}-hint`}
          className="mt-1 text-xs font-normal leading-5 text-slate-500"
        >
          {hint}
        </p>
      )}
    </div>
  );
}
function Badge({
  children,
  good = false,
}: {
  children: ReactNode;
  good?: boolean;
}) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${good ? 'bg-green-50 text-green-800' : 'bg-slate-100 text-slate-700'}`}
    >
      {children}
    </span>
  );
}
function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md bg-slate-50 p-5 text-sm leading-6 text-slate-600">
      {children}
    </p>
  );
}
type Request = {
  consultationId: string;
  email: string;
  name: string;
  attachments: { attachmentId: string; fileName: string }[];
  photoPlanning?: {
    consent: boolean;
    views: {
      view: 'front' | 'rear' | 'left' | 'right';
      attachmentId: string;
    }[];
    skippedViews?: string[];
    notes?: string;
  };
};
type Action = (action: string, body?: Record<string, unknown>) => Promise<any>;

export function ConciergeProjectWorkspace({
  request,
  onStatusChange,
}: {
  request: Request;
  onStatusChange?: (status: string) => void;
}) {
  const api = useApi();
  const [data, setData] = useState<ConciergeProjectPayload | null>(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pendingRequests = useRef(
    new Map<string, { requestId: string; revision: number }>(),
  );
  const dataRef = useRef<ConciergeProjectPayload | null>(null);
  function acceptData(value: ConciergeProjectPayload) {
    dataRef.current = value;
    setData(value);
    onStatusChange?.(value.project.status);
  }
  const path = `/${request.consultationId}/project`;
  async function load() {
    setLoading(true);
    setError('');
    try {
      acceptData(await api.concierge(path));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [request.consultationId]);
  useEffect(() => {
    if (
      !['queued', 'generating'].includes(data?.project.planning?.status || '')
    )
      return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [request.consultationId, data?.project.planning?.status]);
  const action: Action = async (name, body = {}) => {
    setBusy(true);
    setError('');
    setNotice('');
    const operationKey = JSON.stringify({ name, body });
    const operation = pendingRequests.current.get(operationKey) || {
      requestId: crypto.randomUUID(),
      revision: dataRef.current?.project.revision ?? 0,
    };
    pendingRequests.current.set(operationKey, operation);
    try {
      const result = await api.concierge(path, {
        method: 'POST',
        body: JSON.stringify({
          action: name,
          expectedRevision: operation.revision,
          requestId: operation.requestId,
          ...body,
        }),
      });
      pendingRequests.current.delete(operationKey);
      if (result.project) acceptData(result);
      else if (name === 'uploadLibrary') acceptData(await api.concierge(path));
      return result;
    } catch (e) {
      if ([400, 403, 404, 409].includes((e as { status?: number }).status || 0))
        pendingRequests.current.delete(operationKey);
      setError((e as Error).message);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const run = (
    name: string,
    body?: Record<string, unknown>,
    message?: string,
  ) =>
    void action(name, body)
      .then(() => {
        if (message) setNotice(message);
      })
      .catch(() => {});
  async function download(documentId: string, version: number) {
    try {
      const result = await action('downloadDocument', { documentId, version });
      const anchor = document.createElement('a');
      anchor.href = privateProjectUrl(result.url);
      anchor.rel = 'noreferrer';
      anchor.target = '_blank';
      anchor.click();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (loading && !data)
    return (
      <p role="status" className="py-8 text-sm text-slate-500">
        Loading project workspace…
      </p>
    );
  return (
    <div className="mt-7 border-t border-slate-200 pt-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Project workspace</h3>
          <p className={muted}>
            Quotes, people, and the next step for this home.
          </p>
        </div>
        <button
          type="button"
          className={button}
          disabled={busy || loading}
          onClick={() => void load()}
        >
          <RefreshCw size={15} /> Refresh project
        </button>
      </div>
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900"
        >
          <p>{error}</p>
          <p className="mt-2 text-xs">
            If another team member changed this project, refresh before
            retrying. Unsaved edits may need to be entered again.
          </p>
        </div>
      )}
      {notice && (
        <p
          role="status"
          className="mb-4 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900"
        >
          {notice}
        </p>
      )}
      {data && (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            <Badge>{statusLabel(data.project.status)}</Badge>
            <Badge good={!!data.project.installerProposal?.approvedAt}>
              {data.project.installerProposal?.approvedAt
                ? 'Installer approved'
                : 'Installer approval pending'}
            </Badge>
            <Badge>Revision {data.project.revision}</Badge>
          </div>
          <div
            role="tablist"
            aria-label="Project workspace"
            onKeyDown={(e) => {
              const tabs = [
                ...e.currentTarget.querySelectorAll<HTMLButtonElement>(
                  '[role=tab]',
                ),
              ];
              const index = tabs.findIndex((t) => t === document.activeElement);
              const next =
                e.key === 'ArrowRight'
                  ? (index + 1) % tabs.length
                  : e.key === 'ArrowLeft'
                    ? (index + tabs.length - 1) % tabs.length
                    : e.key === 'Home'
                      ? 0
                      : e.key === 'End'
                        ? tabs.length - 1
                        : -1;
              if (next >= 0) {
                e.preventDefault();
                tabs[next].focus();
                tabs[next].click();
              }
            }}
            className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 pb-2"
          >
            {[
              ['overview', 'Overview'],
              ['planning', 'Home plan'],
              ['documents', 'Quotes & invoices'],
              ['installers', 'Installer & handoff'],
              ['library', 'PDF library'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                id={`project-tab-${value}`}
                aria-controls={`project-panel-${value}`}
                aria-selected={tab === value}
                tabIndex={tab === value ? 0 : -1}
                onClick={() => setTab(value)}
                className={`min-h-11 rounded-md px-3 py-2 text-sm font-medium ${tab === value ? 'bg-black text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div>
            <div
              role="tabpanel"
              id="project-panel-planning"
              aria-labelledby="project-tab-planning"
              hidden={tab !== 'planning'}
            >
              <PlanningWorkspace
                request={request}
                data={data}
                action={action}
                busy={busy}
                notify={setNotice}
                onDocuments={() => setTab('documents')}
              />
            </div>
            <div
              role="tabpanel"
              id="project-panel-overview"
              aria-labelledby="project-tab-overview"
              hidden={tab !== 'overview'}
            >
              <form
                key={`overview-${data.project.revision}`}
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  run(
                    'updateProject',
                    {
                      status: f.get('status'),
                      publicUpdate: f.get('publicUpdate'),
                      internalNotes: f.get('internalNotes'),
                      serviceAddress: f.get('serviceAddress'),
                      schedule: {
                        start: f.get('start')
                          ? new Date(String(f.get('start'))).toISOString()
                          : '',
                        end: f.get('end')
                          ? new Date(String(f.get('end'))).toISOString()
                          : '',
                        notes: f.get('scheduleNotes'),
                      },
                    },
                    'Project update saved.',
                  );
                }}
                className="space-y-5"
              >
                <Field label="Project status">
                  <select
                    name="status"
                    className={input}
                    defaultValue={data.project.status || 'received'}
                  >
                    {steps.map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Customer update"
                  hint="Visible to the customer in their Concierge account."
                >
                  <textarea
                    name="publicUpdate"
                    maxLength={4000}
                    rows={3}
                    className={input}
                    defaultValue={data.project.publicUpdate}
                    placeholder="What is happening and what comes next?"
                  />
                </Field>
                <Field label="Service address">
                  <textarea
                    name="serviceAddress"
                    maxLength={1000}
                    rows={2}
                    className={input}
                    defaultValue={data.project.serviceAddress}
                    placeholder="Confirm the full installation address with the customer"
                  />
                </Field>
                <div className="border-t pt-5">
                  <h4 className="mb-3 font-semibold">Installation schedule</h4>
                  <p className={`${muted} mb-3`}>
                    Booking requires customer approval of the quote and the
                    proposed installer.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Starts (your local time)">
                      <input
                        name="start"
                        type="datetime-local"
                        className={input}
                        defaultValue={localDate(data.project.schedule?.start)}
                      />
                    </Field>
                    <Field label="Ends (your local time)">
                      <input
                        name="end"
                        type="datetime-local"
                        className={input}
                        defaultValue={localDate(data.project.schedule?.end)}
                      />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <Field label="Schedule notes for the customer">
                      <textarea
                        name="scheduleNotes"
                        className={input}
                        rows={2}
                        maxLength={1000}
                        defaultValue={data.project.schedule?.notes}
                      />
                    </Field>
                  </div>
                </div>
                <Field
                  label="Private team notes"
                  hint="Only your team can see these notes."
                >
                  <textarea
                    name="internalNotes"
                    maxLength={8000}
                    rows={4}
                    className={input}
                    defaultValue={data.project.internalNotes}
                  />
                </Field>
                <button disabled={busy} className={primary}>
                  Save project update
                </button>
              </form>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const f = new FormData(form);
                  void action('addMessage', { message: f.get('message') })
                    .then(() => {
                      form.reset();
                      setNotice('Message added to the customer’s project.');
                    })
                    .catch(() => {});
                }}
                className="mt-7 border-t pt-5 space-y-3"
              >
                <Field
                  label="Message to the customer"
                  hint="Visible in their project timeline. This does not send an email."
                >
                  <textarea
                    name="message"
                    required
                    maxLength={2000}
                    rows={3}
                    className={input}
                  />
                </Field>
                <button disabled={busy} className={button}>
                  Add customer message
                </button>
              </form>
              <div className="mt-8 border-t pt-6">
                <h4 className="mb-4 font-semibold">Project activity</h4>
                {!data.project.activity?.length ? (
                  <Empty>Saved project actions will appear here.</Empty>
                ) : (
                  <ol className="space-y-4">
                    {[...data.project.activity]
                      .sort((a, b) => b.at.localeCompare(a.at))
                      .map((entry, i) => (
                        <li
                          key={entry.id || i}
                          className="border-l-2 border-slate-200 pl-4"
                        >
                          <p className="text-sm font-medium">
                            {statusLabel(entry.action)}
                            {(entry.message || entry.note) && (
                              <span className="block mt-1 font-normal whitespace-pre-wrap break-words">
                                {String(entry.message || entry.note)}
                              </span>
                            )}
                          </p>
                          <p className="mt-1 break-words text-xs text-slate-500">
                            {date(entry.at)}
                            {entry.actor ? ` · ${entry.actor}` : ''}
                          </p>
                        </li>
                      ))}
                  </ol>
                )}
              </div>
            </div>
            <div
              role="tabpanel"
              id="project-panel-documents"
              aria-labelledby="project-tab-documents"
              hidden={tab !== 'documents'}
            >
              <DocumentWorkspace
                data={data}
                action={action}
                busy={busy}
                email={request.email}
                download={download}
                notify={setNotice}
              />
            </div>
            <div
              role="tabpanel"
              id="project-panel-installers"
              aria-labelledby="project-tab-installers"
              hidden={tab !== 'installers'}
            >
              <InstallerWorkspace
                data={data}
                action={action}
                busy={busy}
                attachments={request.attachments || []}
                notify={setNotice}
              />
            </div>
            <div
              role="tabpanel"
              id="project-panel-library"
              aria-labelledby="project-tab-library"
              hidden={tab !== 'library'}
            >
              <LibraryWorkspace
                data={data}
                action={action}
                busy={busy}
                notify={setNotice}
              />
            </div>
          </div>
        </>
      )}
      {!data && !loading && (
        <button type="button" className={button} onClick={() => void load()}>
          Retry loading project
        </button>
      )}
    </div>
  );
}

function localDate(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function PlanningWorkspace({
  request,
  data,
  action,
  busy,
  notify,
  onDocuments,
}: {
  request: Request;
  data: ConciergeProjectPayload;
  action: Action;
  busy: boolean;
  notify: (text: string) => void;
  onDocuments: () => void;
}) {
  const api = useApi();
  const planning = data.project.planning;
  const [text, setText] = useState(planning?.draftText || '');
  const [dirty, setDirty] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const [photoBusy, setPhotoBusy] = useState('');
  const [photoError, setPhotoError] = useState('');
  const jobRunning = ['queued', 'generating'].includes(planning?.status || '');
  const stalled =
    jobRunning &&
    !!planning?.updatedAt &&
    Date.now() - Date.parse(planning.updatedAt) >= 10 * 60 * 1000;
  const pending = jobRunning && !stalled;
  const consent =
    !!request.photoPlanning?.consent && !!request.photoPlanning.views?.length;
  useEffect(() => {
    if (!dirty) {
      setText(planning?.draftText || '');
      setReviewed(false);
    }
  }, [planning?.draftText, planning?.updatedAt, dirty]);
  async function viewPhoto(attachmentId: string) {
    setPhotoBusy(attachmentId);
    setPhotoError('');
    try {
      const result = await api.concierge(
        `/${request.consultationId}/files/${attachmentId}/download`,
        { method: 'POST', body: '{}' },
      );
      const url = privateProjectUrl(result.url);
      setPhotos((current) => ({ ...current, [attachmentId]: url }));
    } catch (e) {
      setPhotoError((e as Error).message);
    } finally {
      setPhotoBusy('');
    }
  }
  const planningLabel = {
    received: 'Ready to plan',
    queued: 'Draft queued',
    generating: 'Preparing a private draft',
    draft: 'Draft ready for review',
    failed: 'Draft needs attention',
    published: 'Home plan published',
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Home installation plan</h4>
          <p className={`${muted} mt-1`}>
            Review the home, prepare a plan, and confirm the scope before
            quoting.
          </p>
        </div>
        <Badge good={planning?.status === 'published'}>
          {planningLabel[planning?.status || 'received']}
        </Badge>
      </div>
      <section>
        <h5 className="text-sm font-semibold">Exterior views</h5>
        <p className={`${muted} mt-2 mb-4`}>
          These photos are private. Use them to guide a review; confirm
          measurements, cable routes, and installation requirements with the
          customer and local installer.
        </p>
        {photoError && (
          <p role="alert" className="mb-3 text-sm text-red-800">
            {photoError}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {(['front', 'rear', 'left', 'right'] as const)
            .filter((view) =>
              request.photoPlanning?.views?.some((item) => item.view === view),
            )
            .map((view) => {
              const reference = request.photoPlanning?.views?.find(
                (v) => v.view === view,
              );
              const file = request.attachments.find(
                (f) => f.attachmentId === reference?.attachmentId,
              );
              return (
                <div
                  key={view}
                  className="overflow-hidden rounded-md border border-slate-200"
                >
                  <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center">
                    {reference && photos[reference.attachmentId] ? (
                      <img
                        src={photos[reference.attachmentId]}
                        alt={`${view[0].toUpperCase() + view.slice(1)} exterior submitted by ${request.name}`}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="h-full w-full object-contain"
                        onError={() => {
                          setPhotos((current) => {
                            const next = { ...current };
                            delete next[reference.attachmentId];
                            return next;
                          });
                          setPhotoError(
                            'This photo link could not be loaded. Choose View photo to request a fresh link.',
                          );
                        }}
                      />
                    ) : (
                      <p className="px-4 text-center text-sm text-slate-500">
                        {reference
                          ? 'Photo ready to review'
                          : request.photoPlanning?.skippedViews?.includes(view)
                            ? 'Skipped by the customer'
                            : 'No photo submitted'}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {view[0].toUpperCase() + view.slice(1)} of home
                      </p>
                      {file && (
                        <p className="mt-1 break-all text-xs text-slate-500">
                          {file.fileName}
                        </p>
                      )}
                    </div>
                    {reference && (
                      <button
                        type="button"
                        disabled={busy || !!photoBusy}
                        className={button}
                        onClick={() => void viewPhoto(reference.attachmentId)}
                      >
                        {photoBusy === reference.attachmentId
                          ? 'Loading…'
                          : photos[reference.attachmentId]
                            ? 'Refresh photo'
                            : 'View photo'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
        {!request.photoPlanning?.views?.length && (
          <Empty>
            No exterior photos were submitted with this application.
          </Empty>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {(['front', 'rear', 'left', 'right'] as const)
            .filter(
              (view) =>
                !request.photoPlanning?.views?.some(
                  (item) => item.view === view,
                ),
            )
            .map((view) => (
              <Badge key={view}>
                {view[0].toUpperCase() + view.slice(1)}:{' '}
                {request.photoPlanning?.skippedViews?.includes(view)
                  ? 'skipped'
                  : 'not submitted'}
              </Badge>
            ))}
        </div>
        {request.photoPlanning?.notes && (
          <div className="mt-4 border-l-2 border-slate-200 pl-4">
            <p className="text-sm font-medium">Customer’s planning notes</p>
            <p className={`${muted} mt-1 whitespace-pre-wrap break-words`}>
              {request.photoPlanning.notes}
            </p>
          </div>
        )}
      </section>
      <section className="border-t pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h5 className="font-semibold">Private planning draft</h5>
            <p className={`${muted} mt-1`}>
              AI can help prepare an initial draft from the submitted views.
              MANOR reviews the plan before the customer sees it.
            </p>
          </div>
          <button
            type="button"
            className={button}
            disabled={busy || pending || !consent || dirty}
            onClick={() =>
              void action('generatePlan')
                .then(() => {
                  setDirty(false);
                  setReviewed(false);
                  notify(
                    'Planning started. The private draft will appear here for review.',
                  );
                })
                .catch(() => {})
            }
          >
            {pending
              ? 'Preparing draft…'
              : planning?.draftText
                ? 'Generate a fresh draft'
                : 'Generate planning draft'}
          </button>
        </div>
        {!consent && (
          <p className="mt-3 text-sm text-slate-500">
            Exterior photos and the customer’s planning consent are required to
            generate an AI draft. You can still prepare a plan manually.
          </p>
        )}
        {dirty && (
          <p className="mt-3 text-sm text-slate-500">
            Save your edits before generating another draft.
          </p>
        )}
        {pending && (
          <p
            role="status"
            className="mt-4 rounded-md bg-slate-50 p-4 text-sm text-slate-700"
          >
            The draft is being prepared. This page checks for updates
            automatically. You can continue working in the other tabs.
          </p>
        )}
        {stalled && (
          <p
            role="status"
            className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            Draft generation is taking longer than expected. You can retry
            generation or finish the plan manually.
          </p>
        )}
        {planning?.error && (
          <p
            role="alert"
            className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            {planning.error} Review the source photos, retry generation, or
            prepare the draft manually.
          </p>
        )}
        <form
          className="mt-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void action('savePlan', { text })
              .then(() => {
                setDirty(false);
                notify('Private planning draft saved.');
              })
              .catch(() => {});
          }}
        >
          <Field
            label="Plan draft"
            hint="Private to MANOR until published. Include proposed coverage, equipment, network work, open questions, and installer checks."
          >
            <textarea
              rows={16}
              maxLength={20000}
              className={`${input} leading-6`}
              value={text}
              disabled={busy || pending}
              onChange={(e) => {
                setText(e.target.value);
                setDirty(true);
                setReviewed(false);
              }}
              placeholder="Prepare a plan from the customer’s application, photos, and your review."
            />
          </Field>
          <button disabled={busy || pending || !text.trim()} className={button}>
            Save private draft
          </button>
        </form>
      </section>
      <section className="border-t pt-6">
        <h5 className="font-semibold">Publish the reviewed plan</h5>
        <p className={`${muted} mt-2`}>
          Publishing creates a PDF in this customer’s project. Quote pricing and
          installer approval remain separate steps.
        </p>
        <label className="mt-4 flex items-start gap-3 text-sm leading-6">
          <input
            type="checkbox"
            checked={reviewed}
            disabled={busy || pending || !text.trim()}
            onChange={(e) => setReviewed(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0"
          />
          I reviewed this plan for this home, checked unsupported assumptions,
          and confirmed it is ready for the customer.
        </label>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || pending || !reviewed || !text.trim()}
            className={primary}
            onClick={() =>
              void action('publishPlan', { text, reviewed: true })
                .then(() => {
                  setDirty(false);
                  setReviewed(false);
                  notify(
                    'Reviewed home plan published. The customer can view its PDF in their project.',
                  );
                })
                .catch(() => {})
            }
          >
            Publish reviewed plan PDF
          </button>
          {planning?.publishedDocumentId && (
            <button type="button" className={button} onClick={onDocuments}>
              View published PDF
            </button>
          )}
        </div>
        {planning?.reviewedAt && (
          <p className="mt-3 text-xs text-slate-500">
            Last reviewed {date(planning.reviewedAt)}
          </p>
        )}
      </section>
    </div>
  );
}

function DocumentWorkspace({
  data,
  action,
  busy,
  email,
  download,
  notify,
}: {
  data: ConciergeProjectPayload;
  action: Action;
  busy: boolean;
  email: string;
  download: (id: string, version: number) => Promise<void>;
  notify: (s: string) => void;
}) {
  const [editing, setEditing] = useState<{
    id?: string;
    document: ProjectDocumentDraft;
  } | null>(null);
  const [send, setSend] = useState<{
    documentId: string;
    version: number;
    title: string;
  } | null>(null);
  const [payment, setPayment] = useState<ProjectDocument | null>(null);
  const [deliveryReview, setDeliveryReview] = useState<{
    documentId: string;
    version: number;
    title: string;
  } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  async function revise(doc: ProjectDocument, version?: number) {
    try {
      const snapshot = version
        ? (await action('getDocumentVersion', { documentId: doc.id, version }))
            .document
        : doc.draft || doc.versions.at(-1)?.snapshot;
      if (!snapshot)
        throw Error('This version could not be loaded. Refresh and try again.');
      setEditing({ id: doc.id, document: structuredClone(snapshot) });
    } catch (e) {
      notify((e as Error).message);
    }
  }
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Quotes & invoices</h4>
          <p className={muted}>
            Prepare a draft, publish a PDF, then choose when to send it.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            className={button}
            onClick={() => setEditing({ document: blankDocument('quote') })}
          >
            <Plus size={15} /> New quote
          </button>
          <button
            type="button"
            disabled={busy}
            className={button}
            onClick={() => setEditing({ document: blankDocument('invoice') })}
          >
            New invoice
          </button>
        </div>
      </div>
      {editing && (
        <DocumentEditor
          key={editing.id || editing.document.kind}
          initial={editing.document}
          documentId={editing.id}
          data={data}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={async (document) => {
            await action('saveDocument', { documentId: editing.id, document });
            setEditing(null);
            notify('Draft saved. Customer documents have not changed.');
          }}
        />
      )}
      {send && (
        <form
          className="rounded-md border border-slate-400 bg-slate-50 p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void action('sendDocument', {
              documentId: send.documentId,
              version: send.version,
            })
              .then(() => {
                setSend(null);
                notify(
                  'Send request recorded. Check the delivery status below before retrying.',
                );
              })
              .catch(() => {});
          }}
        >
          <h5 className="font-semibold">
            Send {send.title}, version {send.version}
          </h5>
          <p className={muted}>
            An email linking to the published PDF in their account will be sent
            to <strong className="break-all">{email}</strong>.
          </p>
          <label className="flex gap-3 items-start text-sm">
            <input required type="checkbox" className="mt-1 h-5 w-5 shrink-0" />
            I have reviewed this version and the saved customer email.
          </label>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} className={primary}>
              Send to customer
            </button>
            <button
              type="button"
              disabled={busy}
              className={button}
              onClick={() => setSend(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {deliveryReview && (
        <form
          className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void action('resolveDelivery', {
              documentId: deliveryReview.documentId,
              version: deliveryReview.version,
              status: f.get('status'),
              note: f.get('note'),
            })
              .then(() => {
                setDeliveryReview(null);
                notify('Delivery review recorded.');
              })
              .catch(() => {});
          }}
        >
          <h5 className="font-semibold">
            Review {deliveryReview.title}, version {deliveryReview.version}
          </h5>
          <p className={muted}>
            Verify the outcome in email logs or with the customer. Marking a
            message as not sent allows a new send.
          </p>
          <Field label="Verified delivery outcome">
            <select name="status" required className={input} defaultValue="">
              <option value="">Choose a confirmed outcome</option>
              <option value="confirmed-sent">Confirmed sent</option>
              <option value="confirmed-not-sent">Confirmed not sent</option>
            </select>
          </Field>
          <Field label="Delivery review evidence">
            <textarea
              name="note"
              required
              rows={3}
              maxLength={2000}
              className={input}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} className={primary}>
              Save delivery review
            </button>
            <button
              type="button"
              disabled={busy}
              className={button}
              onClick={() => setDeliveryReview(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {payment && (
        <form
          className="rounded-md border p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void action('markInvoicePaid', {
              documentId: payment.id,
              paid: !payment.paid,
              note: f.get('note'),
            })
              .then(() => {
                setPayment(null);
                notify('Invoice payment record updated.');
              })
              .catch(() => {});
          }}
        >
          <h5 className="font-semibold">
            Mark invoice {payment.paid ? 'unpaid' : 'paid'}
          </h5>
          <p className={muted}>
            This records a payment received elsewhere. No payment is collected
            here.
          </p>
          <Field label="Payment reference or reason">
            <input name="note" required maxLength={1000} className={input} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} className={primary}>
              Save payment record
            </button>
            <button
              type="button"
              className={button}
              onClick={() => setPayment(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {!data.project.documents.length && !editing && (
        <Empty>
          Create a quote to start planning this installation. Published
          documents and every revision will remain here.
        </Empty>
      )}
      <div className="divide-y divide-slate-200">
        {data.project.documents.map((doc) => {
          const latest = doc.versions.at(-1);
          return (
            <section key={doc.id} className="py-5 first:pt-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-start gap-2">
                    <FileText
                      size={18}
                      className="mt-0.5 shrink-0 text-slate-500"
                    />
                    <h5 className="break-words font-semibold">{doc.title}</h5>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge>{doc.kind}</Badge>
                    {doc.draft && <Badge>Draft saved</Badge>}
                    {latest?.acceptedAt && (
                      <Badge good>Customer accepted v{latest.version}</Badge>
                    )}
                    {doc.kind === 'invoice' && (
                      <Badge good={doc.paid}>
                        {doc.paid ? 'Paid' : 'Unpaid'}
                      </Badge>
                    )}
                  </div>
                  {latest && (
                    <p className="mt-2 text-sm text-slate-600">
                      Version {latest.version}
                      {doc.kind !== 'guide' &&
                        ` · ${projectMoney(latest.totalCents || 0, latest.currency || 'CAD')}`}{' '}
                      · {date(latest.publishedAt)}
                    </p>
                  )}
                  {doc.draft?.totalCents !== undefined && (
                    <p className="mt-2 text-sm font-medium">
                      Saved draft total:{' '}
                      {projectMoney(doc.draft.totalCents, doc.draft.currency)}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {doc.kind !== 'guide' && (
                    <button
                      type="button"
                      disabled={busy}
                      className={button}
                      onClick={() => void revise(doc)}
                    >
                      Edit draft
                    </button>
                  )}
                  {doc.draft && (
                    <button
                      type="button"
                      disabled={busy || !!editing}
                      title={
                        editing
                          ? 'Save or close the document editor before publishing.'
                          : undefined
                      }
                      className={primary}
                      onClick={() =>
                        void action('publishDocument', { documentId: doc.id })
                          .then(() => {
                            setEditing(null);
                            notify(
                              'PDF published. Send it when you are ready.',
                            );
                          })
                          .catch(() => {})
                      }
                    >
                      Publish PDF
                    </button>
                  )}
                </div>
              </div>
              {latest && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    className={button}
                    onClick={() => void download(doc.id, latest.version)}
                  >
                    <Download size={15} /> Open PDF
                  </button>
                  {doc.kind !== 'guide' && (
                    <button
                      type="button"
                      disabled={
                        busy ||
                        [
                          'sending',
                          'review-needed',
                          'review_required',
                          'sent',
                          'confirmed-sent',
                        ].includes(latest.sendStatus || '')
                      }
                      className={button}
                      onClick={() =>
                        setSend({
                          documentId: doc.id,
                          version: latest.version,
                          title: doc.title,
                        })
                      }
                    >
                      Send version {latest.version}
                    </button>
                  )}
                  {doc.kind === 'invoice' && (
                    <button
                      type="button"
                      disabled={busy}
                      className={button}
                      onClick={() => setPayment(doc)}
                    >
                      Mark {doc.paid ? 'unpaid' : 'paid'}
                    </button>
                  )}
                  <Badge
                    good={['sent', 'confirmed-sent'].includes(
                      latest.sendStatus || '',
                    )}
                  >
                    {latest.sendStatus
                      ? `Email: ${statusLabel(latest.sendStatus)}`
                      : 'Not emailed'}
                  </Badge>
                </div>
              )}
              {latest &&
                ['sending', 'review-needed'].includes(
                  latest.sendStatus || '',
                ) && (
                  <button
                    type="button"
                    disabled={busy}
                    className={`${button} mt-3`}
                    onClick={() =>
                      setDeliveryReview({
                        documentId: doc.id,
                        version: latest.version,
                        title: doc.title,
                      })
                    }
                  >
                    Review delivery
                  </button>
                )}
              {latest?.sendStatus &&
                /review|unknown|uncertain/.test(latest.sendStatus) && (
                  <p className="mt-3 text-sm text-amber-800">
                    Delivery needs review. Check the email record before sending
                    another version.
                  </p>
                )}
              {doc.versions.length > 0 && (
                <>
                  <button
                    type="button"
                    aria-expanded={expanded === doc.id}
                    className="min-h-11 mt-2 text-sm font-medium underline underline-offset-4"
                    onClick={() =>
                      setExpanded(expanded === doc.id ? null : doc.id)
                    }
                  >
                    {expanded === doc.id ? 'Hide' : 'View'} version history (
                    {doc.versions.length})
                  </button>
                  {expanded === doc.id && (
                    <ol className="mt-2 space-y-3 rounded-md bg-slate-50 p-4">
                      {[...doc.versions].reverse().map((version) => (
                        <li
                          key={version.version}
                          className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3 last:border-0 last:pb-0"
                        >
                          <div>
                            <p className="text-sm font-medium">
                              Version {version.version}
                              {doc.kind !== 'guide' &&
                                ` · ${projectMoney(version.totalCents || 0, version.currency || 'CAD')}`}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {date(version.publishedAt)} ·{' '}
                              {statusLabel(version.sendStatus || 'not sent')}
                              {version.acceptedAt ? ' · Customer accepted' : ''}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              className={button}
                              onClick={() =>
                                void download(doc.id, version.version)
                              }
                            >
                              Open PDF
                            </button>
                            {doc.kind !== 'guide' && (
                              <button
                                type="button"
                                disabled={busy}
                                className={button}
                                onClick={() =>
                                  void revise(doc, version.version)
                                }
                              >
                                Revise this version
                              </button>
                            )}
                            {['sending', 'review-needed'].includes(
                              version.sendStatus || '',
                            ) && (
                              <button
                                type="button"
                                disabled={busy}
                                className={button}
                                onClick={() =>
                                  setDeliveryReview({
                                    documentId: doc.id,
                                    version: version.version,
                                    title: doc.title,
                                  })
                                }
                              >
                                Review delivery
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function DocumentEditor({
  initial,
  documentId,
  data,
  busy,
  onClose,
  onSave,
}: {
  initial: ProjectDocumentDraft;
  documentId?: string;
  data: ConciergeProjectPayload;
  busy: boolean;
  onClose: () => void;
  onSave: (d: ProjectDocumentDraft) => Promise<void>;
}) {
  const [doc, setDoc] = useState<ProjectDocumentDraft>(
    structuredClone(initial),
  );
  const [error, setError] = useState('');
  const update = (key: keyof ProjectDocumentDraft, value: unknown) =>
    setDoc((d) => ({ ...d, [key]: value }));
  const quoteVersions = data.project.documents
    .filter((d) => d.kind === 'quote')
    .flatMap((d) =>
      d.versions.map((v) => ({
        id: d.id,
        version: v.version,
        label: `${d.title} · v${v.version}${v.acceptedAt ? ' · accepted' : ''}`,
      })),
    );
  const subtotal = doc.lineItems.reduce(
    (sum, item) => sum + (Math.round(item.quantity * item.unitPriceCents) || 0),
    0,
  );
  const taxes = doc.taxes.reduce(
    (sum, t) => sum + Math.round((subtotal * t.ratePercent) / 100),
    0,
  );
  async function save(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await onSave(doc);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <form
      onSubmit={save}
      className="rounded-lg border border-slate-300 bg-slate-50/60 p-4 sm:p-5 space-y-5"
    >
      <div className="flex items-center justify-between gap-3">
        <h5 className="font-semibold">
          {documentId ? 'Edit' : 'New'} {doc.kind} draft
        </h5>
        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          className={button}
          aria-label="Close document editor"
        >
          <X size={17} />
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_120px]">
        <Field label="Document title">
          <input
            required
            maxLength={160}
            className={input}
            value={doc.title}
            onChange={(e) => update('title', e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <input
            className={input}
            value={doc.currency}
            readOnly
          />
        </Field>
      </div>
      <Field
        label={
          doc.kind === 'invoice'
            ? 'Payment due date (optional)'
            : 'Quote valid until (optional)'
        }
      >
        <input
          type="date"
          className={input}
          value={
            doc.kind === 'invoice' ? doc.dueDate || '' : doc.validUntil || ''
          }
          onChange={(e) =>
            update(
              doc.kind === 'invoice' ? 'dueDate' : 'validUntil',
              e.target.value,
            )
          }
        />
      </Field>
      {doc.kind === 'invoice' && (
        <Field label="Related quote (optional)">
          <select
            className={input}
            value={doc.quoteId ? `${doc.quoteId}:${doc.quoteVersion}` : ''}
            onChange={(e) => {
              const q = quoteVersions.find(
                (q) => `${q.id}:${q.version}` === e.target.value,
              );
              setDoc((d) => ({
                ...d,
                quoteId: q?.id,
                quoteVersion: q?.version,
              }));
            }}
          >
            <option value="">No linked quote</option>
            {quoteVersions.map((q) => (
              <option
                key={`${q.id}:${q.version}`}
                value={`${q.id}:${q.version}`}
              >
                {q.label}
              </option>
            ))}
          </select>
        </Field>
      )}
      <fieldset>
        <legend className="mb-3 text-sm font-semibold">Line items</legend>
        <div className="space-y-4">
          {doc.lineItems.map((item, i) => (
            <div
              key={i}
              className="rounded-md border border-slate-200 bg-white p-3"
            >
              <div className="flex items-start gap-2">
                <Field label={`Item ${i + 1} description`}>
                  <input
                    required
                    maxLength={500}
                    className={input}
                    value={item.description}
                    onChange={(e) =>
                      update(
                        'lineItems',
                        doc.lineItems.map((old, n) =>
                          n === i
                            ? { ...old, description: e.target.value }
                            : old,
                        ),
                      )
                    }
                  />
                </Field>
                <button
                  type="button"
                  disabled={busy || doc.lineItems.length === 1}
                  className={`${button} mt-6 !px-3`}
                  aria-label={`Remove item ${i + 1}`}
                  onClick={() =>
                    update(
                      'lineItems',
                      doc.lineItems.filter((_, n) => n !== i),
                    )
                  }
                >
                  <X size={16} />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Field label="Quantity">
                  <input
                    required
                    type="number"
                    min="0.001"
                    step="0.001"
                    className={input}
                    value={item.quantity || ''}
                    onChange={(e) =>
                      update(
                        'lineItems',
                        doc.lineItems.map((old, n) =>
                          n === i
                            ? { ...old, quantity: Number(e.target.value) }
                            : old,
                        ),
                      )
                    }
                  />
                </Field>
                <Field label="Unit">
                  <input
                    maxLength={30}
                    className={input}
                    value={item.unit || ''}
                    onChange={(e) =>
                      update(
                        'lineItems',
                        doc.lineItems.map((old, n) =>
                          n === i ? { ...old, unit: e.target.value } : old,
                        ),
                      )
                    }
                    placeholder="each, hour, metre"
                  />
                </Field>
                <Field label={`Unit price (${doc.currency})`}>
                  <input
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    className={input}
                    value={
                      Number.isFinite(item.unitPriceCents)
                        ? item.unitPriceCents / 100
                        : ''
                    }
                    onChange={(e) =>
                      update(
                        'lineItems',
                        doc.lineItems.map((old, n) =>
                          n === i
                            ? {
                                ...old,
                                unitPriceCents:
                                  e.target.value === ''
                                    ? Number.NaN
                                    : Math.round(Number(e.target.value) * 100),
                              }
                            : old,
                        ),
                      )
                    }
                  />
                </Field>
              </div>
              <p className="mt-3 text-right text-sm">
                {projectMoney(
                  Math.round(item.quantity * item.unitPriceCents) || 0,
                  doc.currency,
                )}
              </p>
            </div>
          ))}
        </div>
        <button
          type="button"
          className={`${button} mt-3`}
          disabled={busy || doc.lineItems.length >= 40}
          onClick={() =>
            update('lineItems', [
              ...doc.lineItems,
              {
                description: '',
                quantity: 1,
                unit: 'each',
                unitPriceCents: Number.NaN,
              },
            ])
          }
        >
          <Plus size={15} /> Add line item
        </button>
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-sm font-semibold">Taxes</legend>
        <p className={`${muted} mb-3`}>
          Enter the applicable tax labels and rates. No tax jurisdiction is
          selected automatically.
        </p>
        {doc.taxes.map((t, i) => (
          <div key={i} className="mb-3 flex items-end gap-2">
            <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
              <Field label="Tax label">
                <input
                  required
                  maxLength={60}
                  className={input}
                  value={t.label}
                  onChange={(e) =>
                    update(
                      'taxes',
                      doc.taxes.map((old, n) =>
                        n === i ? { ...old, label: e.target.value } : old,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Rate (%)">
                <input
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.001"
                  className={input}
                  value={t.ratePercent}
                  onChange={(e) =>
                    update(
                      'taxes',
                      doc.taxes.map((old, n) =>
                        n === i
                          ? { ...old, ratePercent: Number(e.target.value) }
                          : old,
                      ),
                    )
                  }
                />
              </Field>
            </div>
            <button
              type="button"
              className={`${button} !px-3`}
              aria-label={`Remove tax ${i + 1}`}
              onClick={() =>
                update(
                  'taxes',
                  doc.taxes.filter((_, n) => n !== i),
                )
              }
            >
              <X size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className={button}
          disabled={busy || doc.taxes.length >= 5}
          onClick={() =>
            update('taxes', [...doc.taxes, { label: '', ratePercent: 0 }])
          }
        >
          Add tax
        </button>
      </fieldset>
      <dl className="space-y-2 border-y border-slate-200 py-4 text-sm">
        <div className="flex justify-between gap-4">
          <dt>Subtotal</dt>
          <dd>{projectMoney(subtotal, doc.currency)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Tax</dt>
          <dd>{projectMoney(taxes, doc.currency)}</dd>
        </div>
        <div className="flex justify-between gap-4 font-semibold">
          <dt>Estimated total</dt>
          <dd>{projectMoney(subtotal + taxes, doc.currency)}</dd>
        </div>
        <p className="text-xs text-slate-500">
          The server recalculates all totals when the draft is saved.
        </p>
      </dl>
      <fieldset className="space-y-4">
        <legend className="mb-3 text-sm font-semibold">Issuer details</legend>
        <p className={muted}>
          Legal name, address, and email are required to publish. Invoices also
          need payment instructions; documents with tax need tax registration
          details.
        </p>
        <Field label="Legal business name">
          <input
            maxLength={160}
            className={input}
            value={doc.issuer.name}
            onChange={(e) =>
              update('issuer', { ...doc.issuer, name: e.target.value })
            }
          />
        </Field>
        <Field label="Business address">
          <textarea
            maxLength={1000}
            rows={2}
            className={input}
            value={doc.issuer.address}
            onChange={(e) =>
              update('issuer', { ...doc.issuer, address: e.target.value })
            }
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Business email">
            <input
              type="email"
              maxLength={254}
              className={input}
              value={doc.issuer.email}
              onChange={(e) =>
                update('issuer', { ...doc.issuer, email: e.target.value })
              }
            />
          </Field>
          <Field label="Tax registration (if applicable)">
            <input
              maxLength={200}
              className={input}
              value={doc.issuer.taxRegistration || ''}
              onChange={(e) =>
                update('issuer', {
                  ...doc.issuer,
                  taxRegistration: e.target.value,
                })
              }
            />
          </Field>
        </div>
        <Field label="Payment instructions">
          <textarea
            maxLength={2000}
            rows={2}
            className={input}
            value={doc.issuer.paymentInstructions || ''}
            onChange={(e) =>
              update('issuer', {
                ...doc.issuer,
                paymentInstructions: e.target.value,
              })
            }
          />
        </Field>
      </fieldset>
      <Field label="Customer notes">
        <textarea
          rows={3}
          maxLength={4000}
          className={input}
          value={doc.notes || ''}
          onChange={(e) => update('notes', e.target.value)}
        />
      </Field>
      <Field label="Terms">
        <textarea
          rows={3}
          maxLength={4000}
          className={input}
          value={doc.terms || ''}
          onChange={(e) => update('terms', e.target.value)}
        />
      </Field>
      {error && (
        <p role="alert" className="text-sm text-red-800">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} className={primary}>
          Save draft
        </button>
        <button
          type="button"
          disabled={busy}
          className={button}
          onClick={onClose}
        >
          Close editor
        </button>
      </div>
    </form>
  );
}

function InstallerWorkspace({
  data,
  action,
  busy,
  attachments,
  notify,
}: {
  data: ConciergeProjectPayload;
  action: Action;
  busy: boolean;
  attachments: Request['attachments'];
  notify: (s: string) => void;
}) {
  const [editing, setEditing] = useState<ProjectInstaller | null>(null);
  const proposal = data.project.installerProposal;
  const quoteVersions = data.project.documents
    .filter((d) => d.kind === 'quote')
    .flatMap((d) =>
      d.versions.map((v) => ({
        id: d.id,
        version: v.version,
        accepted: !!v.acceptedAt,
        label: `${d.title} · v${v.version}${v.acceptedAt ? ' · accepted' : ''}`,
      })),
    );
  const approvedQuote = quoteVersions.some(
    (q) =>
      q.id === proposal?.quoteId &&
      q.version === proposal?.quoteVersion &&
      q.accepted,
  );
  return (
    <div className="space-y-7">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-semibold">Installer proposal</h4>
          <Badge good={!!proposal?.approvedAt}>
            {proposal?.approvedAt
              ? `Approved ${date(proposal.approvedAt)}`
              : 'Customer approval required'}
          </Badge>
        </div>
        {proposal && (
          <div className="my-4 border-l-2 border-slate-900 pl-4">
            <p className="font-medium">{proposal.installer.name}</p>
            <p className={`${muted} break-words`}>
              {proposal.installer.publicDescription}
            </p>
            <p className={`${muted} break-all`}>
              {proposal.installer.email} {proposal.installer.phone}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {proposal.quoteId
                ? `Linked quote version ${proposal.quoteVersion}`
                : 'No linked quote'}
              {approvedQuote
                ? ' · Quote accepted'
                : ' · Quote approval pending'}
            </p>
          </div>
        )}
        <form
          key={`proposal-${data.project.revision}`}
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const q = quoteVersions.find(
              (q) => `${q.id}:${q.version}` === f.get('quote'),
            );
            void action('proposeInstaller', {
              installerId: f.get('installerId'),
              quoteId: q?.id,
              quoteVersion: q?.version,
            })
              .then(() =>
                notify('Installer proposal saved for customer approval.'),
              )
              .catch(() => {});
          }}
        >
          <Field label="Local installer">
            <select
              name="installerId"
              required
              className={input}
              defaultValue={proposal?.installer.id || ''}
            >
              <option value="">Choose an installer</option>
              {data.installers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} — {i.serviceArea}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quote for this installation">
            <select
              name="quote"
              required
              className={input}
              defaultValue={
                proposal?.quoteId
                  ? `${proposal.quoteId}:${proposal.quoteVersion}`
                  : ''
              }
            >
              <option value="">Choose a published quote</option>
              {quoteVersions.map((q) => (
                <option
                  key={`${q.id}:${q.version}`}
                  value={`${q.id}:${q.version}`}
                >
                  {q.label}
                </option>
              ))}
            </select>
          </Field>
          <p className={muted}>
            Changing the installer or quote requires fresh customer approval
            before booking or handoff.
          </p>
          <button
            disabled={busy || !data.installers.length || !quoteVersions.length}
            className={primary}
          >
            Propose installer to customer
          </button>
        </form>
      </section>
      <section className="border-t pt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-semibold">Installer directory</h4>
            <p className={muted}>
              Client contact details are kept separate from team notes.
            </p>
          </div>
          <button
            type="button"
            className={button}
            disabled={busy}
            onClick={() => setEditing(blankInstaller())}
          >
            <Plus size={15} /> Add installer
          </button>
        </div>
        {editing && (
          <form
            className="mb-5 rounded-md border bg-slate-50 p-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void action('saveInstaller', { installer: editing })
                .then(() => {
                  setEditing(null);
                  notify(
                    'Installer saved to the directory. Existing proposals keep the details the customer reviewed.',
                  );
                })
                .catch(() => {});
            }}
          >
            <h5 className="font-semibold">
              {editing.id ? 'Edit installer' : 'New installer'}
            </h5>
            <Field label="Business name">
              <input
                required
                maxLength={160}
                className={input}
                value={editing.name}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Client contact email">
                <input
                  required
                  type="email"
                  className={input}
                  value={editing.email}
                  onChange={(e) =>
                    setEditing({ ...editing, email: e.target.value })
                  }
                />
              </Field>
              <Field label="Client contact phone">
                <input
                  required
                  type="tel"
                  maxLength={60}
                  className={input}
                  value={editing.phone}
                  onChange={(e) =>
                    setEditing({ ...editing, phone: e.target.value })
                  }
                />
              </Field>
            </div>
            <Field label="Service area">
              <input
                required
                maxLength={300}
                className={input}
                value={editing.serviceArea}
                onChange={(e) =>
                  setEditing({ ...editing, serviceArea: e.target.value })
                }
              />
            </Field>
            <Field label="Website (optional)">
              <input
                type="url"
                maxLength={500}
                className={input}
                value={editing.website}
                onChange={(e) =>
                  setEditing({ ...editing, website: e.target.value })
                }
                placeholder="https://"
              />
            </Field>
            <Field label="Description for customers">
              <textarea
                rows={2}
                maxLength={2000}
                className={input}
                value={editing.publicDescription}
                onChange={(e) =>
                  setEditing({ ...editing, publicDescription: e.target.value })
                }
              />
            </Field>
            <Field label="Private installer notes">
              <textarea
                rows={3}
                maxLength={4000}
                className={input}
                value={editing.internalNotes}
                onChange={(e) =>
                  setEditing({ ...editing, internalNotes: e.target.value })
                }
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <button disabled={busy} className={primary}>
                Save installer
              </button>
              <button
                type="button"
                className={button}
                disabled={busy}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        {!data.installers.length && (
          <Empty>
            Add an installer you have sourced locally. You can then include them
            in a customer proposal.
          </Empty>
        )}
        <ul className="divide-y">
          {data.installers.map((i) => (
            <li
              key={i.id}
              className="flex flex-wrap justify-between items-center gap-3 py-4"
            >
              <div className="min-w-0">
                <p className="font-medium break-words">{i.name}</p>
                <p className={muted}>{i.serviceArea}</p>
                <p className="text-xs text-slate-500 break-all">
                  {i.email} · {i.phone}
                </p>
              </div>
              <button
                type="button"
                disabled={busy}
                className={button}
                onClick={() => setEditing(structuredClone(i))}
              >
                Edit details
              </button>
            </li>
          ))}
        </ul>
      </section>
      <section className="border-t pt-6">
        <h4 className="font-semibold">Installation handoff</h4>
        <p className={`${muted} mt-2 mb-4`}>
          Record the agreed handoff and completed setup. Only files the customer
          approved for sharing can be selected.
        </p>
        {(!proposal?.approvedAt || !approvedQuote) && (
          <p className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            Handoff is locked until the customer approves this installer and the
            linked quote.
          </p>
        )}
        <form
          key={`handoff-${data.project.revision}`}
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void action('handoff', {
              sharedAttachmentIds: f.getAll('attachment'),
              checklist: {
                equipmentInstalled: f.has('equipmentInstalled'),
                manorConfigured: f.has('manorConfigured'),
                householdWalkthrough: f.has('householdWalkthrough'),
              },
              note: f.get('handoffNote'),
            })
              .then(() => notify('Installation handoff saved.'))
              .catch(() => {});
          }}
        >
          <fieldset
            disabled={busy || !proposal?.approvedAt || !approvedQuote}
            className="space-y-4 disabled:opacity-60"
          >
            <legend className="mb-3 text-sm font-medium">
              Files approved by the customer
            </legend>
            {!attachments.length && (
              <p className={muted}>No customer files were submitted.</p>
            )}
            {attachments.map((f) => {
              const allowed = proposal?.sharedAttachmentIds?.includes(
                f.attachmentId,
              );
              return (
                <label
                  key={f.attachmentId}
                  className="flex gap-3 items-start text-sm"
                >
                  <input
                    name="attachment"
                    type="checkbox"
                    value={f.attachmentId}
                    disabled={!allowed}
                    defaultChecked={data.project.handoff?.sharedAttachmentIds?.includes(
                      f.attachmentId,
                    )}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                  <span className="min-w-0 break-all">
                    {f.fileName}
                    <span className="block text-xs text-slate-500">
                      {allowed
                        ? 'Customer approved sharing'
                        : 'Sharing not approved'}
                    </span>
                  </span>
                </label>
              );
            })}
            <div className="border-t pt-4 space-y-4">
              {[
                ['equipmentInstalled', 'Equipment installed and connected'],
                [
                  'manorConfigured',
                  'Node prepared, flashed, and enrolled in MANOR',
                ],
                [
                  'householdWalkthrough',
                  'System tested and household walkthrough complete',
                ],
              ].map(([key, label]) => (
                <label key={key} className="flex items-start gap-3 text-sm">
                  <input
                    name={key}
                    type="checkbox"
                    defaultChecked={data.project.handoff?.checklist?.[key]}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                  {label}
                </label>
              ))}
            </div>
            <Field
              label="Handoff brief and outcome"
              hint="Shared with the customer. Keep access codes and private notes out of this field."
            >
              <textarea
                name="handoffNote"
                rows={4}
                maxLength={4000}
                className={input}
                defaultValue={data.project.handoff?.note}
              />
            </Field>
            <button
              disabled={busy || !proposal?.approvedAt || !approvedQuote}
              className={primary}
            >
              <Check size={15} /> Save handoff
            </button>
          </fieldset>
        </form>
      </section>
    </div>
  );
}

function LibraryWorkspace({
  data,
  action,
  busy,
  notify,
}: {
  data: ConciergeProjectPayload;
  action: Action;
  busy: boolean;
  notify: (s: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [attach, setAttach] = useState<ProjectLibraryFile | null>(null);
  async function preview(libraryId: string) {
    try {
      const result = await action('downloadLibrary', { libraryId });
      const anchor = document.createElement('a');
      anchor.href = privateProjectUrl(result.url);
      anchor.target = '_blank';
      anchor.rel = 'noreferrer';
      anchor.click();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function upload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const file = f.get('file');
    if (!(file instanceof File) || !file.size) return;
    setUploading(true);
    setError('');
    try {
      if (
        file.type !== 'application/pdf' &&
        !file.name.toLowerCase().endsWith('.pdf')
      )
        throw Error('Choose a PDF document.');
      if (file.size > 15 * 1024 * 1024)
        throw Error('Choose a PDF no larger than 15 MB.');
      const hash = new Uint8Array(
        await crypto.subtle.digest('SHA-256', await file.arrayBuffer()),
      );
      const checksumSHA256 = btoa(String.fromCharCode(...hash));
      const result = await action('uploadLibrary', {
        fileName: file.name,
        size: file.size,
        checksumSHA256,
        category: f.get('category'),
        title: f.get('title'),
      });
      const post = new FormData();
      for (const [key, value] of Object.entries(result.fields))
        post.append(key, String(value));
      post.append('file', file);
      const response = await fetch(privateProjectUrl(result.url), {
        method: 'POST',
        body: post,
      });
      if (!response.ok)
        throw Error('The PDF upload failed. Choose the file and retry.');
      await action('completeLibrary', { uploadId: result.uploadId });
      form.reset();
      notify('PDF added to the library. Attach it to this project when ready.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }
  const files = data.library.filter(
    (f) =>
      (!category || category === f.category) &&
      `${f.title} ${f.fileName}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold">Reusable PDF library</h4>
        <p className={`${muted} mt-1`}>
          Find previous Wi-Fi, camera, and setup documents. Attach a reviewed
          PDF to make it available in this customer’s account.
        </p>
      </div>
      <details className="rounded-md border border-slate-200">
        <summary className="min-h-11 cursor-pointer p-4 text-sm font-medium">
          Add a PDF to the library
        </summary>
        <form
          onSubmit={(e) => void upload(e)}
          className="border-t p-4 space-y-4"
        >
          <Field label="Document title">
            <input name="title" required maxLength={160} className={input} />
          </Field>
          <Field label="Category">
            <select name="category" className={input}>
              <option value="wifi">Wi-Fi setup</option>
              <option value="camera">Camera setup</option>
              <option value="other">Other document</option>
            </select>
          </Field>
          <Field
            label="PDF file"
            hint="PDF only, up to 15 MB. Review previous client documents and remove personal information before reusing them."
          >
            <input
              name="file"
              required
              type="file"
              accept="application/pdf,.pdf"
              className={`${input} !p-2`}
            />
          </Field>
          {error && (
            <p role="alert" className="text-sm text-red-800">
              {error}
            </p>
          )}
          <button disabled={busy || uploading} className={primary}>
            {uploading ? 'Uploading PDF…' : 'Upload PDF'}
          </button>
        </form>
      </details>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Find a document">
          <input
            type="search"
            className={input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search titles and filenames"
          />
        </Field>
        <Field label="Category">
          <select
            className={input}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All categories</option>
            <option value="wifi">Wi-Fi setup</option>
            <option value="camera">Camera setup</option>
            <option value="other">Other documents</option>
          </select>
        </Field>
      </div>
      {!files.length && (
        <Empty>
          {data.library.length
            ? 'No documents match these filters.'
            : 'Your library is empty. Upload an approved PDF to reuse it across Concierge projects.'}
        </Empty>
      )}
      {attach && (
        <form
          className="rounded-md border border-slate-400 p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void action('attachLibrary', {
              libraryId: attach.id,
              confirmClientSafe: true,
            })
              .then(() => {
                setAttach(null);
                notify(
                  'PDF attached to this customer’s project. Open it from Quotes & invoices.',
                );
              })
              .catch(() => {});
          }}
        >
          <h5 className="font-semibold">
            Share {attach.title} with this customer?
          </h5>
          <p className={muted}>
            The complete PDF becomes visible in the customer’s account.
          </p>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" required className="mt-1 h-5 w-5 shrink-0" />
            I reviewed this PDF for this customer. It contains no other client’s
            information and is safe to share.
          </label>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy || uploading} className={primary}>
              Attach reviewed PDF
            </button>
            <button
              type="button"
              className={button}
              onClick={() => setAttach(null)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-800">
          {error}
        </p>
      )}
      <ul className="divide-y">
        {files.map((file) => (
          <li
            key={file.id}
            className="flex flex-wrap items-start justify-between gap-3 py-4"
          >
            <div className="min-w-0">
              <p className="font-medium break-words">{file.title}</p>
              {file.internalReference && (
                <span className="mt-2 inline-flex">
                  <Badge>Private client reference</Badge>
                </span>
              )}
              <p className="text-xs text-slate-500 break-all mt-1">
                {file.fileName} ·{' '}
                {file.size < 1024 * 1024
                  ? `${Math.max(1, Math.ceil(file.size / 1024))} KB`
                  : `${(file.size / 1024 / 1024).toFixed(1)} MB`}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {file.category === 'wifi'
                  ? 'Wi-Fi setup'
                  : file.category === 'camera'
                    ? 'Camera setup'
                    : 'Other'}{' '}
                · Added {date(file.createdAt)}
              </p>
              {file.internalReference && (
                <p className="mt-2 max-w-prose text-xs leading-5 text-slate-500">
                  For your team’s reference. This client document cannot be
                  shared with another customer.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy || uploading}
                className={button}
                onClick={() => void preview(file.id)}
              >
                Review PDF
              </button>
              {!file.internalReference && (
                <button
                  type="button"
                  disabled={busy || uploading}
                  className={button}
                  onClick={() => setAttach(file)}
                >
                  Attach to project
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
